import { useState, useEffect } from "react";
import { TrendingUp, Users, Sparkles } from "lucide-react";
import FinPlayLogo from "./FinPlayLogo";

interface SplashScreenProps {
  onComplete: () => void;
}

const featureSegments = [
  { 
    name: "Fin-der", 
    tagline: "Swipe & Learn", 
    icon: Sparkles, 
    color: "hsl(170 80% 50%)",
    angle: 0 
  },
  { 
    name: "League", 
    tagline: "Trade & Compete", 
    icon: TrendingUp, 
    color: "hsl(45 90% 55%)",
    angle: 120 
  },
  { 
    name: "Community", 
    tagline: "Discuss & Grow", 
    icon: Users, 
    color: "hsl(280 70% 60%)",
    angle: 240 
  },
];

const SplashScreen = ({ onComplete }: SplashScreenProps) => {
  const [phase, setPhase] = useState<'loading' | 'interactive' | 'exit'>('loading');
  const [rotation, setRotation] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [interactionCount, setInteractionCount] = useState(0);
  const [particles] = useState<Array<{ id: number; x: number; y: number; delay: number; hue: number; size: number }>>(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 3,
      hue: 170 + Math.random() * 110,
      size: 1 + Math.random() * 2.5,
    }))
  );

  const orbitTrails = [0, 1, 2];

  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase('interactive');
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  // Auto-rotate slowly when interactive
  useEffect(() => {
    if (phase !== 'interactive') return;
    
    const interval = setInterval(() => {
      setRotation(prev => (prev + 0.3) % 360);
    }, 50);
    
    return () => clearInterval(interval);
  }, [phase]);

  // Update active segment based on rotation
  useEffect(() => {
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    const newIndex = Math.floor((normalizedRotation + 60) / 120) % 3;
    setActiveIndex(newIndex);
  }, [rotation]);

  const handleInteraction = () => {
    if (phase !== 'interactive') return;
    
    // Spin faster on click
    setRotation(prev => prev + 120);
    setInteractionCount(prev => prev + 1);
    
    // After 1 interaction, enter the app
    if (interactionCount >= 0) {
      setTimeout(() => {
        setPhase('exit');
        setTimeout(onComplete, 600);
      }, 400);
    }
  };

  const handleSkip = () => {
    setPhase('exit');
    setTimeout(onComplete, 400);
  };

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center overflow-hidden transition-all duration-600
        ${phase === 'exit' ? 'opacity-0 scale-110 pointer-events-none' : 'opacity-100 scale-100'}`}
      style={{
        background: 'linear-gradient(135deg, hsl(220 25% 6%) 0%, hsl(240 20% 4%) 50%, hsl(220 25% 6%) 100%)',
      }}
    >

      {/* Aurora glow backdrop */}
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background: `
            radial-gradient(circle at 30% 30%, ${featureSegments[0].color}22 0%, transparent 45%),
            radial-gradient(circle at 70% 70%, ${featureSegments[1].color}22 0%, transparent 45%),
            radial-gradient(circle at 50% 50%, ${featureSegments[2].color}1a 0%, transparent 55%)
          `,
          animation: 'aurora-shift 8s ease-in-out infinite',
        }}
      />

      {/* Floating particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            background: `hsl(${particle.hue} 80% 65%)`,
            animation: `float-particle 6s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`,
            boxShadow: `0 0 ${8 + particle.size * 3}px currentColor`,
          }}
        />
      ))}

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-6">
        
        {/* Rotating Feature Circle */}
        <div 
          className={`relative cursor-pointer transition-all duration-700
            ${phase === 'loading' ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`}
          onClick={handleInteraction}
          style={{ width: '320px', height: '320px' }}
        >
          {/* Counter-rotating orbit trails */}
          {orbitTrails.map((i) => (
            <div
              key={`trail-${i}`}
              className="absolute rounded-full border pointer-events-none"
              style={{
                inset: `${i * 14}px`,
                borderColor: `${featureSegments[i].color}33`,
                borderWidth: '1px',
                animation: `orbit-spin ${18 + i * 4}s linear infinite ${i % 2 === 0 ? '' : 'reverse'}`,
                boxShadow: `0 0 20px ${featureSegments[i].color}15 inset`,
              }}
            />
          ))}

          {/* Outer rotating ring with segments */}
          <div 
            className="absolute inset-0 rounded-full transition-transform duration-500 ease-out"
            style={{ 
              transform: `rotate(${rotation}deg)`,
            }}
          >
            {/* Gradient ring */}
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(
                  from 0deg,
                  ${featureSegments[0].color} 0deg,
                  ${featureSegments[0].color} 115deg,
                  ${featureSegments[1].color} 125deg,
                  ${featureSegments[1].color} 235deg,
                  ${featureSegments[2].color} 245deg,
                  ${featureSegments[2].color} 355deg,
                  ${featureSegments[0].color} 360deg
                )`,
                padding: '4px',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'xor',
                WebkitMaskComposite: 'xor',
              }}
            />
            
            {/* Feature icons on the ring */}
            {featureSegments.map((segment, index) => {
              const Icon = segment.icon;
              const angleRad = ((segment.angle - 90) * Math.PI) / 180;
              const radius = 140;
              const x = Math.cos(angleRad) * radius;
              const y = Math.sin(angleRad) * radius;
              
              return (
                <div
                  key={segment.name}
                  className="absolute flex items-center justify-center transition-all duration-300"
                  style={{
                    left: '50%',
                    top: '50%',
                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${-rotation}deg)`,
                  }}
                >
                  <div 
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      activeIndex === index ? 'scale-125 ' : 'scale-100'
                    }`}
                    style={{ 
                      background: segment.color,
                      boxShadow: activeIndex === index ? `0 0 30px ${segment.color}` : 'none',
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Inner glow effect */}
          <div 
            className="absolute inset-12 rounded-full blur-2xl opacity-40 transition-all duration-500"
            style={{
              background: `radial-gradient(circle, ${featureSegments[activeIndex].color} 0%, transparent 70%)`,
            }}
          />

          {/* Center logo container - using new FinPlayLogo component */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className="w-36 h-36 rounded-full flex items-center justify-center"
              style={{
                background: 'radial-gradient(circle, hsl(220 20% 12%) 0%, hsl(220 25% 8%) 100%)',
                boxShadow: `inset 0 0 30px ${featureSegments[activeIndex].color}20, 0 0 40px hsl(0 0% 0% / 0.5)`,
              }}
            >
              <FinPlayLogo 
                size="xl" 
                animated 
                className={`transition-all duration-500 ${phase === 'interactive' ? 'animate-pulse-gentle' : ''}`}
              />
            </div>
          </div>

          {/* Click ripple effect */}
          {phase === 'interactive' && (
            <div 
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                border: `2px solid ${featureSegments[activeIndex].color}`,
                animation: 'ripple 2s ease-out infinite',
              }}
            />
          )}
        </div>

        {/* Active feature info */}
        <div 
          className={`text-center transition-all duration-500 ${
            phase === 'loading' ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
          }`}
        >
          <h2 
            className="text-2xl font-semibold mb-1 transition-colors duration-300"
            style={{ color: featureSegments[activeIndex].color }}
          >
            {featureSegments[activeIndex].name}
          </h2>
          <p className="text-muted-foreground text-lg">
            {featureSegments[activeIndex].tagline}
          </p>
        </div>

        {/* Interaction prompt */}
        {phase === 'interactive' && (
          <div className="flex flex-col items-center gap-3 animate-fade-in">
            <p className="text-sm text-muted-foreground/70">
              Tap to enter
            </p>
            <div className="flex gap-2">
              {[0].map((i) => (
                <div 
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i < interactionCount 
                      ? 'bg-primary scale-110' 
                      : 'bg-muted-foreground/30'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {phase === 'loading' && (
          <div className="w-48 h-1 bg-muted/30 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, ${featureSegments[0].color}, ${featureSegments[1].color}, ${featureSegments[2].color})`,
                animation: 'loading-bar 1.2s ease-out forwards',
              }}
            />
          </div>
        )}

        {/* Tagline */}
        <p 
          className={`text-lg text-muted-foreground font-medium tracking-wide transition-all duration-500 delay-200 ${
            phase === 'loading' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          Master Money. <span className="text-secondary">Play Smart.</span>
        </p>
      </div>

      {/* Skip button */}
      <button
        onClick={handleSkip}
        className={`absolute bottom-8 right-8 text-sm text-muted-foreground hover:text-muted-foreground 
          transition-all duration-300 ${phase === 'interactive' ? 'opacity-100' : 'opacity-0'}`}
      >
        Skip →
      </button>

      <style>{`
        @keyframes float-particle {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.35; }
          25% { transform: translate(8px, -18px) scale(1.2); opacity: 0.7; }
          50% { transform: translate(-6px, -28px) scale(1.4); opacity: 0.9; }
          75% { transform: translate(4px, -14px) scale(1.1); opacity: 0.6; }
        }

        @keyframes aurora-shift {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.6; }
          50% { transform: scale(1.15) rotate(8deg); opacity: 0.85; }
        }

        @keyframes orbit-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes loading-bar {
          0% { width: 0%; }
          100% { width: 100%; }
        }

        @keyframes ripple {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.35); opacity: 0; }
        }

        .animate-pulse-gentle {
          animation: pulse-gentle 2.4s ease-in-out infinite;
        }

        @keyframes pulse-gentle {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 8px hsl(170 80% 50% / 0.3)); }
          50% { transform: scale(1.06); filter: drop-shadow(0 0 18px hsl(170 80% 50% / 0.6)); }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
